import React, { useEffect, useState } from 'react'
import { PayPalNamespace, loadScript } from '@paypal/paypal-js'
import { HostedFieldsHandler } from '@paypal/paypal-js/types/components/hosted-fields'
import { OrderProps } from '../types/OrderProps'
import { api } from '../services/api'
import { sendMessage } from 'services/messenger'
import { Spinner } from '@welbe/web-design-system'
import { loggerInfo } from 'utils/infoLogs'
import { PayPalForm } from './PayPalForm/PayPalForm'
import { PayPalConfirmation } from './PayPalConfirmation/PayPalConfirmation'
import { LoadingWrapper } from '../PaymentCheckout.styles'

export default function Checkout({
	order,
	credentials,
}: {
	order: OrderProps
	credentials: { apiKey: string; token: string }
}) {
	const [paypalOrder, setPaypalOrder] = useState<any>({})
	const [errorMessage, setErrorMessage] = useState('')
	const [clientToken, setClientToken] = useState('')
	const [paypal, setPaypal] = useState<PayPalNamespace | null>(null)
	const [hostedFields, setHostedFields] = useState<HostedFieldsHandler | null>(
		null,
	)
	const [installmentOptions, setInstallmentOptions] = useState<Array<any>>([])
	const [installmentSelected, setInstallmentSelected] = useState<string>('')
	const [loadedHostedFields, setLoadedHostedFields] = useState<boolean>(false)
	const [invalidFields, setInvalidFields] = useState<string[]>([])
	const [emptyFields, setEmptyFields] = useState<string[]>([])
	const http = api(credentials)

	useEffect(() => {
		const createOrder = async () => {
			setPaypalOrder(await handleCreateOrder(order))
		}
		const updateClientToken = async () => {
			try {
				const responseClientToken = await api(credentials).getClientToken()
				if (!responseClientToken) {
					throw new Error('Error al conectar con el procesador de pagos')
				}
				setClientToken(responseClientToken)
			} catch (e: any) {
				sendMessage({
					status: 'error',
					data: {
						message:
							e.message || 'Error al conectar con el procesador de pagos',
					},
				})
				console.error(e)
			}
		}

		createOrder()
		updateClientToken()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	useEffect(() => {
		if (clientToken && Object.keys(paypalOrder).length !== 0) {
			loadScript({
				'client-id': process.env.REACT_APP_PAYPAL_CLIENT_ID || '',
				components: 'hosted-fields',
				'data-client-token': clientToken,
				intent: 'capture',
				vault: true,
				currency: 'MXN',
			})
				.then((pp: PayPalNamespace | null) => {
					setPaypal(pp)
				})
				.catch((e: any) => {
					sendMessage({
						status: 'error',
						data: {
							message:
								e.message || 'Error al conectar con el procesador de pagos',
						},
					})
					console.error(e)
				})
		}
	}, [clientToken, paypalOrder])

	useEffect(() => {
		if (loadedHostedFields) return

		if (paypal) {
			renderHostedFields()
			setLoadedHostedFields(true)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [paypal])

	const getPayload = (order: any) => {
		return {
			currency: order.currency,
			items: order.items.map((item: any) => {
				const itemPayload: any = {
					type: item.type,
					description: item.description,
					clinicalServiceTypeId: item.clinicalServiceTypeId,
					amount: item.amount,
				}

				if (item.examId) itemPayload.examId = item.examId
				if (item.specialtyId) itemPayload.specialtyId = item.specialtyId
				if (item.beneficiaryId) itemPayload.beneficiaryId = item.beneficiaryId
				if (item.data) itemPayload.data = item.data
				if (item.email) itemPayload.email = item.email
				if (!item.beneficiaryId)
					itemPayload.beneficiaryData = item.beneficiaryData
				return itemPayload
			}),
		}
	}

	const handleCreateOrder = async (order: any): Promise<string> => {
		const payload: any = getPayload(order)
		try {
			const response: any = await http.createOrder({ ...payload })
			if (!response.content) {
				throw new Error('Error al crear orden')
			}
			sendMessage({
				status: 'message',
				data: {
					message: 'created-order',
					order: response.content,
					cmid: response.content.payments[0].contextId,
				},
			})
			loggerInfo({
				data: response,
				type: 'info',
				context: 'payment-checkout',
				location: 'renderHostedFields',
				payloadRequest: paypalOrder,
			})
			return response.content
		} catch (error: any) {
			loggerInfo({
				data: error,
				context: 'payment-checkout',
				location: 'handleCreateOrder',
				payloadRequest: payload,
			})
			sendMessage({
				status: 'error',
				data: {
					message: error.message || 'Error al crear orden',
					order: payload,
				},
			})
			console.error(`Error al crear orden`, error)
			return ''
		}
	}

	if (!paypal) {
		return (
			<LoadingWrapper>
				<Spinner size="34px" />
			</LoadingWrapper>
		)
	}

	const handleCaptureOrder = async (): Promise<any> => {
		try {
			const { content }: any = await http.captureOrder(
				paypalOrder?.payments[0]?.orderId,
			)
			const hasError =
				!content ||
				content?.status === 'error' ||
				content?.payments?.every((payment: any) => payment.status === 'error')

			if (hasError) {
				const message =
					content && content?.statusDescription
						? content?.statusDescription
						: 'No se puede procesar el pago, inténtelo de nuevo más tarde'

				loggerInfo({
					data: content,
					context: 'payment-checkout',
					location: 'handleCaptureOrderProcessing',
					payloadRequest: paypalOrder,
				})

				return sendMessage({
					status: 'error',
					data: {
						message: 'capture-error',
						error: message,
					},
				})
			}

			if (content && content.status === 'canceled') {
				loggerInfo({
					data: content,
					context: 'payment-checkout',
					location: 'handleCaptureOrderCanceled',
					payloadRequest: paypalOrder,
				})
				setErrorMessage(
					content?.statusDescription ||
						'Verifique la información de su tarjeta o intente nuevamente con otra tarjeta',
				)
				return
			}

			sendMessage({
				status: 'completed',
				data: { order: content, installmentSelected },
			})
			return content
		} catch (error: any) {
			loggerInfo({
				data: error,
				context: 'payment-checkout',
				location: 'handleCaptureOrder',
				payloadRequest: paypalOrder,
			})
			sendMessage({
				status: 'error',
				data: { message: error.message || 'Error al capture orden' },
			})
			console.error('Error al capture orden', error)
		}
	}

	const onInstallmentsAvailable = (installments: any) => {
		const qualifyingOptions =
			installments &&
			installments.financing_options &&
			installments.financing_options.filter((financialOption: any) => {
				return financialOption?.product === 'CARD_ISSUER_INSTALLMENTS'
			})

		const hasCardIssuerInstallment = Boolean(
			qualifyingOptions &&
				qualifyingOptions.length >= 1 &&
				qualifyingOptions[0].qualifying_financing_options.length >= 1,
		)

		if (!hasCardIssuerInstallment) {
			setErrorMessage('No es posible utilizar esta tarjeta para el pago.')
			return
		}

		const installmentArray: any[] = []
		qualifyingOptions.forEach((financialOption: any) => {
			financialOption.qualifying_financing_options.forEach((o: any) => {
				const label = `${o.credit_financing.term}x $${o.monthly_payment.value}`
				installmentArray.push({
					key: `${o.credit_financing.interval_duration}-${o.monthly_payment.value}`,
					label,
					data: {
						currency_code: o.monthly_payment.currency_code,
						interval: o.credit_financing.interval,
						term: o.credit_financing.term,
						interval_duration: o.credit_financing.interval_duration,
						discount_percentage: o.discount_percentage,
					},
				})
			})
		})

		setInstallmentOptions(installmentArray)
	}

	const onInstallmentsError = (err: any) => {
		loggerInfo({
			data: err,
			context: 'payment-checkout',
			location: 'onInstallmentsError',
			payloadRequest: paypalOrder,
		})
		sendMessage({
			status: 'error',
			data: {
				message: err?.message || 'No se pudo crear la solicitud de pago',
			},
		})
	}

	const renderHostedFields = () => {
		try {
			if (!paypal || !paypal?.HostedFields?.isEligible()) {
				throw new Error('Not eligible for hosted fields')
			}
			paypal.HostedFields.render({
				styles: {
					'.valid': { color: '#8DC63F' },
					'.invalid': { color: '#dc3545' },
					input: {},
				},
				createOrder: () => paypalOrder?.payments[0]?.providerId,
				// eslint-disable-next-line @typescript-eslint/ban-ts-comment
				// @ts-ignore
				paymentsSDK: true,
				debug: false,
				installments: {
					onInstallmentsRequested: function () {
						return {
							amount: paypalOrder.value,
							currencyCode: paypalOrder.currency || 'MXN',
						}
					},
					onInstallmentsAvailable,
					onInstallmentsError,
				},
				fields: {
					number: {
						selector: '#card-number',
						placeholder: 'Escribe los 16 dígitos',
					},
					cvv: {
						selector: '#cvv',
						placeholder: '000',
					},
					expirationDate: {
						selector: '#expiration-date',
						placeholder: 'MM / YYYY',
					},
				},
			})
				.then((hf: HostedFieldsHandler | null) => {
					setHostedFields(hf)
					loggerInfo({
						data: hf,
						type: 'info',
						context: 'payment-checkout',
						location: 'renderHostedFields',
						payloadRequest: paypalOrder,
					})
				})
				.catch((e: any) => {
					loggerInfo({
						data: e,
						context: 'payment-checkout',
						location: 'renderHostedFields',
						payloadRequest: paypalOrder,
					})
					sendMessage({
						status: 'error',
						data: {
							message:
								e?.message || 'Error al conectar con el procesador de pagos',
						},
					})
					console.error(e)
				})

			return <></>
		} catch (e: any) {
			sendMessage({
				status: 'error',
				data: {
					message: e?.message || 'Error',
				},
			})
			return undefined
		}
	}
	return (
		<PayPalForm invalidFields={invalidFields} emptyFields={emptyFields}>
			<PayPalConfirmation
				hostedFields={hostedFields}
				installmentOptions={installmentOptions}
				installmentSelected={installmentSelected}
				handleCaptureOrder={handleCaptureOrder}
				setInstallmentSelected={setInstallmentSelected}
				onErrorMsg={(msg: string) => setErrorMessage(msg)}
				onValidFields={(fields: any) => setInvalidFields(fields)}
				onEmptyFields={(fields: any) => setEmptyFields(fields)}
				errorMessage={errorMessage}
				paypalOrder={paypalOrder}
			/>
		</PayPalForm>
	)
}
